import { type ComponentProps, type FC } from "react";
import { useTranslation } from "react-i18next";
import MultiMetricBarChart from "src/components/reusable/MultiMetricBarChart";
import StyledDropdown from "src/components/reusable/StyledDropdown";
import { DefinitionTooltip } from "src/components/common";
import { getEngagementTooltipInfo } from "src/layouts/Engagements/Overview/Index";
import type { CampaignDashboardText } from "./CampaignDashboardTypes";

interface SelectOption {
  value: string;
  label: string;
}

interface CampaignComparisonChartProps {
  text: CampaignDashboardText;
  chartData: ComponentProps<typeof MultiMetricBarChart>["chartData"];
  selectedMetrics: string[];
  metricOptions: SelectOption[];
  campaignOptions: SelectOption[];
  sourceOptions: SelectOption[];
  campaignIds: string[];
  sources: string[];
  onCampaignIdsChange: (_ids: string[]) => void;
  onSourcesChange: (_sources: string[]) => void;
  formatPercentValue: (_value: number, _fractionDigits?: number) => string;
}

const CampaignComparisonChart: FC<CampaignComparisonChartProps> = ({
  text,
  chartData,
  selectedMetrics,
  metricOptions,
  campaignOptions,
  sourceOptions,
  campaignIds,
  sources,
  onCampaignIdsChange,
  onSourcesChange,
  formatPercentValue,
}) => {
  const { t: translate } = useTranslation();
  const metricLabel = (metricKey: string) =>
    metricOptions.find(opt => opt.value === metricKey)?.label || metricKey;

  return (
    <div className="campaign-chart-card">
      <div className="campaign-chart-header">
        <div className="campaign-chart-title">
          <span>{text.campaignComparison}</span>
          <DefinitionTooltip
            label={text.campaignComparison}
            info={getEngagementTooltipInfo(translate, "campaignPerformance")}
            formulaTitle={translate("engagementOverview.formula")}
          />
        </div>
        <div className="campaign-chart-header-side">
          <div className="campaign-chart-controls">
            <StyledDropdown
              mode="multiple"
              placeholder={text.filterAllCampaigns}
              value={campaignIds}
              onChange={(value: string[]) => onCampaignIdsChange(value ?? [])}
              showSearch
              optionFilterProp="label"
              maxTagCount={1}
              maxTagTextLength={12}
              virtual={false}
              className="filter-dropdown campaign-compare-select"
              popupClassName="filter-dropdown-menu campaign-dashboard__compare-menu"
              options={campaignOptions}
            />
            {sourceOptions.length > 0 && (
              <StyledDropdown
                mode="multiple"
                placeholder={text.filterSource}
                value={sources}
                onChange={(value: string[]) => onSourcesChange(value ?? [])}
                showSearch={false}
                maxTagCount={1}
                maxTagTextLength={12}
                virtual={false}
                className="filter-dropdown campaign-compare-source"
                popupClassName="filter-dropdown-menu campaign-dashboard__compare-menu"
                options={sourceOptions}
              />
            )}
          </div>
          <div className="chart-legend">
            {selectedMetrics.map(metricKey => (
              <div key={metricKey} className="chart-legend-item">
                <span className={`chart-legend-dot chart-legend-dot--${metricKey}`} />
                <span className="chart-legend-label">{metricLabel(metricKey)}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
      <MultiMetricBarChart
        chartData={chartData}
        metrics={selectedMetrics.map(metricKey => ({
          key: metricKey,
          label: metricLabel(metricKey),
        }))}
        tickFormatter={(value: number) => formatPercentValue(value, 0)}
        formatTooltip={(value: number) => formatPercentValue(value, 2)}
      />
    </div>
  );
};

export default CampaignComparisonChart;
